import React, { useState, useEffect } from "react";
import { Card } from "react-bootstrap";
import { useCallManager } from "../../../hooks/useCallManager";
import { useRecording } from "../../../hooks/useRecording";
import CallStatusComponent from "../../atoms/CallStatus/CallStatus";
import {
  CallStatusType,
  MicrophonePermission,
} from "../../../constants/callStatusConstants";

interface FloatingCallWidgetProps {
  phoneNumber: string;
  onClose: (recordingData?: CallRecordingData) => void;
}

interface CallRecordingData {
  recordingUrl?: string;
  audioFile?: File | null;
  audioBlob?: Blob;
  duration: number;
  callTime: string;
  phoneNumber: string;
  status?: CallStatusType;
}

const FloatingCallWidget: React.FC<FloatingCallWidgetProps> = ({
  phoneNumber,
  onClose,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);

  //CALL MANAGER (ACS)
  const {
    callState,
    isMuted,
    microphonePermission,
    toggleMicrophone,
    endCall,
    sendDtmfTone,
    localStream,
    remoteAudioStreams,
  } = useCallManager(phoneNumber);

  // Handle DTMF tone click
  const handleDtmfClick = async (tone: string) => {
    if (callState.status !== CallStatusType.CONNECTED) return;
    await sendDtmfTone(tone);
  };

  //RECORDING
  const { recordingData, startRecording, stopRecording, setRecordingData } =
    useRecording(phoneNumber);

  /**
   * Start recording when:
   * - Call is CONNECTED
   * - Microphone stream is ready
   * - At least one remote audio stream exists
   */
  useEffect(() => {
    if (
      callState.status === CallStatusType.CONNECTED &&
      localStream &&
      remoteAudioStreams.length > 0
    ) {
      startRecording(localStream, remoteAudioStreams).catch(console.error);
    }
  }, [callState.status, localStream, remoteAudioStreams]);

  /**
   * ⏹ Stop recording when call ends
   */
  useEffect(() => {
    if (callState.status === CallStatusType.ENDED) {
      stopRecording();
    }
  }, [callState.status]);

  /**
   * Sync recording metadata with call data
   */
  useEffect(() => {
    if (recordingData && callState.startTime) {
      if (
        recordingData.duration !== callState.duration ||
        recordingData.callTime !== callState.startTime.toISOString()
      ) {
        setRecordingData({
          ...recordingData,
          duration: callState.duration,
          callTime: callState.startTime.toISOString(),
        });
      }
    }
  }, [
    callState.duration,
    callState.startTime,
    recordingData,
    setRecordingData,
  ]);

  const handleEndCall = async () => {
    await endCall();
  };

  const handleClose = () => {
    onClose(recordingData || undefined);
  };

  const handleToggleMinimize = () => {
    setIsMinimized((prev) => !prev);
  };

  return (
    <div className={`simple-call-widget ${isMinimized ? "minimized" : ""}`}>
      <Card className="call-widget-card">
        <Card.Body className="floating-call-body">
          <CallStatusComponent
            phoneNumber={phoneNumber}
            callStatus={callState.status}
            duration={callState.duration}
            isMinimized={isMinimized}
            onToggleMinimize={handleToggleMinimize}
          />

          {!isMinimized && (
            <>
              {/* 🔊 During call */}
              {(callState.status === CallStatusType.CONNECTING ||
                callState.status === CallStatusType.CONNECTED) && (
                  <div className="simple-call-controls">

                    <button
                      className={`control-btn mute-btn ${isMuted ? "muted" : ""
                        }`}
                      onClick={toggleMicrophone}
                      disabled={
                        microphonePermission !== MicrophonePermission.GRANTED
                      }
                      title={isMuted ? "Unmute" : "Mute"}
                    />

                    <button
                      className="control-btn end-call-btn"
                      onClick={handleEndCall}
                      title="End Call"
                    />


                    <div>
                      <div className="dtmf-keypad">
                        {[
                          "1",
                          "2",
                          "3",
                          "4",
                          "5",
                          "6",
                          "7",
                          "8",
                          "9",
                          "*",
                          "0",
                          "#",
                        ].map((tone) => (
                          <button
                            key={tone}
                            className="dtmf-key"
                            disabled={callState.status !== CallStatusType.CONNECTED}
                            onClick={() => handleDtmfClick(tone)}
                          >
                            {tone}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

              {/* After call ends */}
              {callState.status === CallStatusType.ENDED && (
                <div className="recording-upload-section">
                  {recordingData?.audioFile && !recordingData.recordingUrl ? (
                    <button
                      className="control-btn close-btn"
                      onClick={handleClose}
                      title="Close Widget"
                    />
                  ) : recordingData?.recordingUrl ? (
                    <>
                      <div className="text-success text-center">
                        <i className="fas fa-check-circle me-2"></i>
                        Recording saved successfully
                      </div>
                      <button
                        className="control-btn close-btn"
                        onClick={handleClose}
                        title="Close Widget"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </>
                  ) : (
                    <button
                      className="control-btn close-btn"
                      onClick={handleClose}
                      title="Close Widget"
                    />
                  )}
                </div>
              )}

              {/* Call failed */}
              {callState.status === CallStatusType.FAILED && (
                <div className="close-only-section">
                  <button
                    className="control-btn close-btn"
                    onClick={handleClose}
                    title="Close Widget"
                  />
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default FloatingCallWidget;
